import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { structuredDataGenerator } from '@/utils/structuredData';
import { getDomainUtils } from '@/utils/domainUtils';

// Mock blog data - in a real application this would come from an API or CMS
const blogArticles = [
	{
		id: 1,
		title: 'Getting Started with Web Development in 2025',
		excerpt: 'Learn the essential skills and tools needed to begin your web development journey in 2025.',
		image: 'https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
		date: '2025-05-15',
		author: 'Moudondi Godfried',
		slug: 'getting-started-web-development-2025',
		keywords: ['web development', 'programming', 'beginners', 'HTML', 'CSS', 'JavaScript'],
	},
	{
		id: 2,
		title: 'The Future of AI in Web Design',
		excerpt: 'Explore how artificial intelligence is transforming the way we design and build websites.',
		image: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
		date: '2025-05-10',
		author: 'Moudondi Godfried',
		slug: 'future-ai-web-design',
		keywords: ['AI', 'artificial intelligence', 'web design', 'automation', 'future'],
	},
	{
		id: 3,
		title: 'Responsive Design Best Practices',
		excerpt: 'A comprehensive guide to creating responsive websites that work flawlessly across all devices.',
		image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
		date: '2025-05-05',
		author: 'Moudondi Godfried',
		slug: 'responsive-design-best-practices',
		keywords: ['responsive design', 'mobile', 'CSS', 'best practices', 'web development'],
	},
];

const Blog = () => {
	const { t, i18n } = useTranslation();
	const currentLang = i18n.language;
	const { getAlternateLanguages, getCanonicalUrl, getCurrentUrl, getImageUrl } = getDomainUtils();

	const structuredData = structuredDataGenerator.generateContextualSchema('website', null, currentLang);

	const alternateLanguages = getAlternateLanguages('/blog');

	return (
		<div className="min-h-screen flex flex-col">
			<SEOHead
				title={
					currentLang === 'fr'
						? 'Blog - Moudondi Godfried | Articles sur le développement web'
						: 'Blog - Moudondi Godfried | Web Development Articles'
				}
				description={
					currentLang === 'fr'
						? 'Découvrez mes articles sur le développement web, les technologies modernes, React, TypeScript et les meilleures pratiques du développement frontend.'
						: 'Explore my articles on web development, modern technologies, React, TypeScript, and frontend development best practices.'
				}
				keywords={
					currentLang === 'fr'
						? 'blog développement web, articles React, TypeScript, JavaScript, développement frontend, tutoriels web'
						: 'web development blog, React articles, TypeScript, JavaScript, frontend development, web tutorials'
				}
				canonical={getCanonicalUrl('/blog')}
				ogUrl={getCurrentUrl('/blog')}
				ogImage={getImageUrl('/assets/images/blog-og-image.jpg')}
				ogType="website"
				lang={currentLang}
				alternateLanguages={alternateLanguages}
				structuredData={structuredData}
			/>

			<Navbar />
			<div className="section flex-grow pt-32">
				<div className="container-custom">
					<div className="max-w-2xl mx-auto text-center mb-16">
						<h1 className="text-4xl md:text-5xl font-bold mb-4 dark:text-white">
							{t('blogPage.title', 'Blog')}
						</h1>
						<p className="text-lg text-developer-neutral dark:text-gray-300">
							{t('blogPage.description', 'Thoughts, ideas, and insights on web development and design.')}
						</p>
					</div>

					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
						{blogArticles.map((article) => (
							<Link
								to={`/blog/${article.id}`}
								key={article.id}
								className="transition-transform duration-300 hover:scale-[1.02]"
							>
								<Card className="h-full overflow-hidden border border-gray-200 dark:border-gray-700 dark:bg-developer-dark">
									<div className="aspect-[16/9] overflow-hidden">
										<img
											src={article.image}
											alt={article.title}
											className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
											loading="lazy"
										/>
									</div>
									<CardHeader>
										<CardTitle className="dark:text-white">{article.title}</CardTitle>
										<CardDescription className="dark:text-gray-300">
											{new Date(article.date).toLocaleDateString()} • {article.author}
										</CardDescription>
									</CardHeader>
									<CardContent>
										<p className="text-developer-neutral dark:text-gray-300">{article.excerpt}</p>
									</CardContent>
									<CardFooter>
										<p className="text-developer-accent dark:text-developer-accent-light font-medium text-sm">
											{t('blogPage.readMore', 'Read More')} →
										</p>
									</CardFooter>
								</Card>
							</Link>
						))}
					</div>
				</div>
			</div>
			<Footer />
		</div>
	);
};

export default Blog;
